import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';

type AdminJob = {
  id: string;
  title: string;
  status: string;
  totalBudgetRM: number | null;
  estimatedKOL: number | null;
  brandOrg: { id: string; name: string };
  managedByOrg: { id: string; name: string } | null;
  _count: { invitations: number; scripts: number; submissions: number };
  createdAt: string;
};

type JobsResponse = {
  items: AdminJob[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
};

const statuses = ['all', 'DRAFT', 'OPEN', 'ACTIVE', 'PAUSED', 'COMPLETED', 'ARCHIVED'] as const;

export default function AdminCampaigns() {
  const [searchParams] = useSearchParams();
  const agencyOrgIdFromUrl = searchParams.get('agencyOrgId') || '';

  const [items, setItems] = useState<AdminJob[]>([]);
  const [status, setStatus] = useState<(typeof statuses)[number]>('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<JobsResponse['pagination']>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });

  useEffect(() => {
    let active = true;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.get<JobsResponse>('/admin/jobs', {
          params: {
            status: status === 'all' ? undefined : status,
            page,
            limit: 20,
            agencyOrgId: agencyOrgIdFromUrl || undefined,
          },
        });
        if (!active) return;
        setItems(data.items);
        setPagination(data.pagination);
      } catch (err: any) {
        if (!active) return;
        setError(err.response?.data?.error || 'Failed to load campaigns');
      } finally {
        if (active) setLoading(false);
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [status, page, agencyOrgIdFromUrl]);

  const stats = useMemo(() => {
    const visibleBudget = items.reduce((sum, i) => sum + (i.totalBudgetRM || 0), 0);
    const visibleInvites = items.reduce((sum, i) => sum + (i._count?.invitations || 0), 0);
    return {
      total: pagination.total,
      visible: items.length,
      visibleBudget,
      visibleInvites,
    };
  }, [items, pagination.total]);

  return (
    <div className="space-y-6 text-glass-text">
      <div>
        <h1 className="page-title">Campaign Management</h1>
        <p className="text-glass-text-2 text-sm mt-1">All campaigns across agencies and brands</p>
        {agencyOrgIdFromUrl && (
          <p className="mt-2 text-sm text-accent2">
            Filtered by agency org ID: <span className="font-mono">{agencyOrgIdFromUrl}</span>
          </p>
        )}
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-500/10 border border-red-500/30 rounded-lg p-3">{error}</div>
      )}

      <div className="grid md:grid-cols-4 gap-4">
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Total Campaigns</div>
          <div className="stat-display text-2xl mt-1">{stats.total}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Visible</div>
          <div className="stat-display text-2xl mt-1 text-accent2">{stats.visible}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Visible Budget (RM)</div>
          <div className="stat-display text-2xl mt-1 text-accent2">{stats.visibleBudget.toLocaleString()}</div>
        </div>
        <div className="glass-card p-5">
          <div className="text-sm text-glass-text-2">Invitations</div>
          <div className="stat-display text-2xl mt-1 text-[rgb(var(--pos))]">{stats.visibleInvites}</div>
        </div>
      </div>

      <div className="glass-card p-4">
        <select
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value as (typeof statuses)[number]);
          }}
          className="glass-input w-full px-4 py-2"
        >
          {statuses.map((s) => (
            <option key={s} value={s}>
              {s === 'all' ? 'All statuses' : s}
            </option>
          ))}
        </select>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-glass bg-glass-2/30">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Campaign</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Brand</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Agency</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Budget</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Progress Inputs</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-glass-text-3 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-glass">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-glass-text-2">
                    Loading campaigns...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-glass-text-2">
                    No campaigns found.
                  </td>
                </tr>
              ) : (
                items.map((job) => (
                  <tr key={job.id} className="hover:bg-glass-3/40 transition-colors">
                    <td className="px-4 py-4">
                      <div className="font-medium text-glass-text">{job.title}</div>
                      <div className="text-xs text-glass-text-3">{new Date(job.createdAt).toLocaleDateString()}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-glass-text-2">{job.brandOrg?.name || '-'}</td>
                    <td className="px-4 py-4 text-sm text-glass-text-2">{job.managedByOrg?.name || '-'}</td>
                    <td className="px-4 py-4 text-sm font-semibold text-accent2">
                      {(job.totalBudgetRM || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-xs text-glass-text-2">
                      Invites: {job._count?.invitations || 0} | Scripts: {job._count?.scripts || 0} | Submissions:{' '}
                      {job._count?.submissions || 0}
                    </td>
                    <td className="px-4 py-4">
                      <span className="status-pill status-pill-info">{job.status}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-4 py-3 border-t border-glass flex items-center justify-between text-sm text-glass-text-2">
          <span>
            Page {pagination.page} of {pagination.totalPages} ({pagination.total} campaigns)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={pagination.page <= 1}
              className="btn-glass px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={pagination.page >= pagination.totalPages}
              className="btn-glass px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
